import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@/lib/apiClient";
import type { KnowledgeBaseRecord } from "@/lib/knowledgeBaseRegistry";
import type { KnowledgeCustomBook } from "@/lib/knowledgeBookRegistry";
import type { KnowledgeCustomLibrary } from "@/lib/knowledgeLibraryRegistry";

export type KnowledgeRegistryPayload = {
  bases: KnowledgeBaseRecord[];
  /** Tombstoned base ids; do not rediscover or re-upload these. */
  deletedBaseIds?: string[];
  activeBaseId: string;
  librariesByBase: Record<string, KnowledgeCustomLibrary[]>;
  booksByBase: Record<string, KnowledgeCustomBook[]>;
  updatedAt?: string;
};

function normalizeDeletedBaseIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const ids = new Set<string>();
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const id = item.trim();
    if (id) ids.add(id);
  }
  return [...ids];
}

export async function fetchKnowledgeRegistry(): Promise<
  { ok: true; data: KnowledgeRegistryPayload } | { ok: false; error: string; status: number }
> {
  const res = await apiGet<KnowledgeRegistryPayload>("/api/knowledge/registry");
  if (!res.ok || !res.data) {
    return { ok: false, error: res.error || "Could not load knowledge registry.", status: res.status };
  }
  return {
    ok: true,
    data: {
      bases: Array.isArray(res.data.bases) ? res.data.bases : [],
      deletedBaseIds: normalizeDeletedBaseIds(res.data.deletedBaseIds),
      activeBaseId: typeof res.data.activeBaseId === "string" ? res.data.activeBaseId : "",
      librariesByBase: res.data.librariesByBase || {},
      booksByBase: res.data.booksByBase || {},
      updatedAt: res.data.updatedAt,
    },
  };
}

export async function apiCreateKnowledgeBase(input: {
  name: string;
  description?: string;
  id?: string;
}) {
  return apiPost<{ base: KnowledgeBaseRecord; activeBaseId?: string; updatedAt?: string }>(
    "/api/knowledge/registry/bases",
    input,
  );
}

export async function apiUpdateKnowledgeBase(
  baseId: string,
  input: { name: string; description?: string },
) {
  return apiPatch<{ base: KnowledgeBaseRecord; updatedAt?: string }>(
    `/api/knowledge/registry/bases/${encodeURIComponent(baseId)}`,
    input,
  );
}

export async function apiDeleteKnowledgeBase(baseId: string) {
  return apiDelete<{ ok: boolean; activeBaseId?: string; updatedAt?: string }>(
    `/api/knowledge/registry/bases/${encodeURIComponent(baseId)}`,
  );
}

export async function apiSetActiveKnowledgeBase(activeBaseId: string) {
  return apiPut<{ activeBaseId: string; updatedAt?: string }>(
    "/api/knowledge/registry/active-base",
    { activeBaseId },
  );
}

export async function apiCreateKnowledgeLibrary(
  baseId: string,
  input: { title: string; description?: string; id?: string },
) {
  return apiPost<{ library: KnowledgeCustomLibrary; updatedAt?: string }>(
    `/api/knowledge/registry/bases/${encodeURIComponent(baseId)}/libraries`,
    input,
  );
}

export async function apiUpdateKnowledgeLibrary(
  baseId: string,
  libraryId: string,
  input: { title: string; description?: string },
) {
  return apiPatch<{ library: KnowledgeCustomLibrary; updatedAt?: string }>(
    `/api/knowledge/registry/bases/${encodeURIComponent(baseId)}/libraries/${encodeURIComponent(libraryId)}`,
    input,
  );
}

export async function apiDeleteKnowledgeLibrary(baseId: string, libraryId: string) {
  return apiDelete<{ ok: boolean; updatedAt?: string }>(
    `/api/knowledge/registry/bases/${encodeURIComponent(baseId)}/libraries/${encodeURIComponent(libraryId)}`,
  );
}

export async function apiCreateKnowledgeBook(
  baseId: string,
  input: { title: string; description?: string; collectionKey?: string; libraryId?: string },
) {
  return apiPost<{ book: KnowledgeCustomBook; updatedAt?: string }>(
    `/api/knowledge/registry/bases/${encodeURIComponent(baseId)}/books`,
    input,
  );
}

export async function apiUpdateKnowledgeBook(
  baseId: string,
  collectionKey: string,
  input: { title: string; description?: string; libraryId?: string },
) {
  return apiPatch<{ book: KnowledgeCustomBook; updatedAt?: string }>(
    `/api/knowledge/registry/bases/${encodeURIComponent(baseId)}/books/${encodeURIComponent(collectionKey)}`,
    input,
  );
}

export async function apiDeleteKnowledgeBook(baseId: string, collectionKey: string) {
  return apiDelete<{ ok: boolean; updatedAt?: string }>(
    `/api/knowledge/registry/bases/${encodeURIComponent(baseId)}/books/${encodeURIComponent(collectionKey)}`,
  );
}
